/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { FormContext } from './Form';
import Heading from './Heading';
import { generateId } from '../utility/functions';
import GetState, { loadingRadioState } from '../GetState';
import TooltipHelper from './TooltipHelper';
import { Error, OptionalFlag } from './Input';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 0.5em;
`;

const RadioWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 1em;
    border-radius: 2em;
    transition: 0.2s;
    background: ${({ checked, theme }) => checked ? (theme.gray + '45') : ''};

    &:hover {
        background: ${({ checked, theme }) => !checked ? (theme.gray + '1a') : ''};
    }

`;

const RadioCSS = styled.label`
    cursor: pointer;
    width: 1em;
    height: 1em;
    background-color: ${({ theme }) => theme.gray};
    padding: 0.3em 0.3em;
    font-size: inherit;
    display: block;
	border-radius: 100px;
	position: relative;
    border: 1px solid ${({ theme }) => `${theme.gray}40`};
    transition: all 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;

    &:after {
        content: '';
        position: relative;
        left: 0%;
        width: 0.5em;
        height: 0.5em;
        background: ${({ theme }) => theme.input.background};
        border-radius: 90px;
        transition: 0.3s;
    }

    &:disabled {
        color: ${({ theme }) => theme.input.disabledText};
        background-color: ${({ theme }) => theme.input.disabled};
        transform: unset;
        box-shadow: unset;
        cursor: default;
    }
    
`;

const InputCSS = styled.input`
    opacity: 0;
    position: absolute;
    cursor: inherit;
    &:checked + label {
        background: ${({ theme }) => theme.active};
    }

    &:checked + label:after {
        width: 1em;
        height: 1em;
    }

    &:disabled + label:after {
        background: ${({ theme }) => theme.gray}40;
    }
    &:focus + label {
        outline: none !important;
        border: 1px solid ${({ theme }) => theme.text};
        box-shadow: 0 0 3px ${({ theme }) => theme.highlight};
    }
`;


const Radio = ({ context, ...props }) => {
    const [invalid, setInvalid] = useState(false);
    const [invalidMessage, setInvalidMessage] = useState('');

    useLayoutEffect(() => {
        if (context && props.required && !props.disabled) {
            setInvalidation(context.values[props.name] ? false : true)
        }
    }, [])

    const handleChange = (e) => {
        e = e.target.value;
        if (!isNaN(e)) {
            e = parseFloat(e)
        }

        let invalid = false;
        if (e === '' && props.required && !props.disabled) {
            setInvalidMessage('Please complete this field.')
            invalid = true;
        }
        setInvalidation(invalid)

        context.setValues(v => {
            return {
                ...v,
                [props.name]: e
            }
        })

        if (props.callback)
            props.callback(e)
    }

    const setInvalidation = (invalid) => {
        setInvalid(invalid);
        context.setErrors(v => {
            return {
                ...v,
                [props.name]: invalid
            }
        });
    }

    return props.standalone ? (
        <Wrapper>
            <Heading>
                {props.label}
                {context && <TooltipHelper formName={context.formName} name={props.name} />}
            </Heading>
            {
                (props.options || GetState(props.getOptions, { callback: props.buildOptions, loading: loadingRadioState })[0])?.map((e, i) => {
                    const id = generateId();
                    return (
                        <RadioWrapper key={`${props.name}-${i}`} checked={context ? context.values[props.name] === e.value : props.value === e.value}>
                            {props.label && <Heading fontSize={0.8} htmlFor={id} style={{ width: '100%', lineHeight: '2em', cursor: 'pointer' }}>
                                - {e.label}
                                {context && !props.required && <OptionalFlag>- Optional</OptionalFlag>}
                                {invalid && <Error>{invalidMessage || props.error}</Error>}
                            </Heading>}
                            <InputCSS
                                type='radio'
                                value={e.value}
                                checked={context ? context.values[props.name] === e.value : props.value === e.value}
                                onChange={context && handleChange}
                                name={props.name}
                                id={id}
                                disabled={context ? context.submitting || context.editing || props.disable : props.disable}
                                {...props}
                            />
                            <RadioCSS
                                htmlFor={id}
                                disabled={context ? context.submitting || context.editing || props.disable : props.disable}
                                {...props}
                            />
                        </RadioWrapper>
                    )
                })
            }
        </Wrapper>
    ) : (
        <RadioContext
            standalone={1}
            {...props}
        />
    );
}

export default Radio;


const RadioContext = ({ ...props }) => {
    const context = useContext(FormContext);
    return (
        <Radio
            context={{ ...context }}
            {...props}
        />
    )
}
